/**
 * 高信頼性音声録音ユーティリティ
 * iOS/Android対応のMediaRecorder API実装
 */

// 対応コーデックの優先順位
const CODEC_PRIORITIES = [
  'audio/webm; codecs=opus',      // Chrome系（最優先）
  'audio/mp4; codecs=mp4a.40.2',  // iOS/Safari
  'audio/webm',                   // フォールバック1
  'audio/mp4',                    // フォールバック2
  'audio/wav'                     // 最終フォールバック
]

// コーデック別の推奨設定
const CODEC_CONFIGS = {
  'audio/webm; codecs=opus': {
    audioBitsPerSecond: 48000,  // 48kbps
    mimeType: 'audio/webm; codecs=opus'
  },
  'audio/mp4; codecs=mp4a.40.2': {
    audioBitsPerSecond: 64000,  // 64kbps
    mimeType: 'audio/mp4; codecs=mp4a.40.2'
  },
  'audio/webm': {
    audioBitsPerSecond: 48000,
    mimeType: 'audio/webm'
  },
  'audio/mp4': {
    audioBitsPerSecond: 64000,
    mimeType: 'audio/mp4'
  },
  'audio/wav': {
    audioBitsPerSecond: 128000,
    mimeType: 'audio/wav'
  }
}

/**
 * 利用可能なコーデックを検出し、最適なものを選択
 * @returns {Object} 選択されたコーデック設定
 */
export function detectOptimalCodec() {
  console.log('🔍 コーデック検出開始')
  
  for (const codec of CODEC_PRIORITIES) {
    if (MediaRecorder.isTypeSupported(codec)) {
      const config = CODEC_CONFIGS[codec]
      console.log(`✅ 対応コーデック発見: ${codec}`)
      
      // WebM/MP4の種別を判定
      const type = codec.includes('webm') ? 'webm' : 
                   codec.includes('mp4') ? 'mp4' : 'unknown'
      
      return {
        type: type,                           // 🆕 処理分岐用
        mimeType: config.mimeType,
        audioBitsPerSecond: config.audioBitsPerSecond,
        codec: codec
      }
    }
  }
  
  // フォールバック: デフォルト設定（WebM）
  console.warn('⚠️ 対応コーデックが見つかりません。デフォルト設定を使用します')
  return {
    type: 'webm',                            // 🆕 フォールバックはWebM
    mimeType: 'audio/webm',
    audioBitsPerSecond: 48000,
    codec: 'fallback'
  }
}

/**
 * 音声ストリームの設定を取得
 * @returns {Object} 音声ストリーム設定
 */
export function getAudioConstraints() {
  return {
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      sampleRate: 44100,
      channelCount: 1
    }
  }
}

/**
 * デバイス情報を取得
 * @returns {Object} デバイス情報
 */
export function getDeviceInfo() {
  const userAgent = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(userAgent)
  const isAndroid = /Android/.test(userAgent)
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent)
  const isChrome = /Chrome/.test(userAgent)
  
  return {
    isIOS,
    isAndroid,
    isSafari,
    isChrome,
    userAgent
  }
}

/**
 * 録音品質の推奨設定を取得
 * @param {string} codec 選択されたコーデック
 * @returns {Object} 録音品質設定
 */
export function getRecordingQuality(codec) {
  const deviceInfo = getDeviceInfo()
  
  // 全デバイスで3秒チャンクに統一
  return {
    timeslice: 3000,  // 3秒チャンク（全デバイス統一）
    maxDuration: 1200000,  // 20分制限
    chunkSize: deviceInfo.isIOS || deviceInfo.isSafari ? 1024 * 1024 : 2 * 1024 * 1024  // デバイス別チャンクサイズ
  }
}
